import { useCallback, useEffect, useMemo, useState } from 'react';
import { VStack } from '@cbhq/cds-web/layout';
import { Text } from '@cbhq/cds-web/typography/Text';
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';
import { defineMessages, useIntl } from '@cbhq/i18n';

import { useOrganization } from '../../../contexts/OrganizationContext';
import type { SettingsFieldType } from '../components/EditFieldModal';
import { EditFieldModal } from '../components/EditFieldModal';
import { EntityTabs, ORGANIZATION_TAB_ID } from '../components/EntityTabs';
import { SettingsRow } from '../components/SettingsRow';

const messages = defineMessages({
  title: {
    id: 'Settings.home.title',
    defaultMessage: 'Settings',
    description: 'Title for the workspace Settings page',
  },
  nameLabel: {
    id: 'Settings.home.nameLabel',
    defaultMessage: 'Display Name',
    description: 'Label for the organization or legal entity name setting',
  },
});

export function SettingsPage() {
  const { formatMessage } = useIntl();
  const { selectedOrganization } = useOrganization();
  const [activeTabId, setActiveTabId] = useState(ORGANIZATION_TAB_ID);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    logEvent('settings_page_viewed', {
      componentType: ComponentType.page,
      action: ActionType.view,
    });
  }, []);

  useEffect(() => {
    setActiveTabId(ORGANIZATION_TAB_ID);
  }, [selectedOrganization?.organizationId]);

  const legalEntities = useMemo(
    () => selectedOrganization?.legalEntities ?? [],
    [selectedOrganization?.legalEntities],
  );

  const name = useMemo(() => {
    if (activeTabId === ORGANIZATION_TAB_ID) {
      return selectedOrganization?.organizationName ?? '';
    }

    return legalEntities.find((le) => le.legalEntityId === activeTabId)?.legalEntityName ?? '';
  }, [activeTabId, legalEntities, selectedOrganization?.organizationName]);

  const nameLabel = useMemo(() => formatMessage(messages.nameLabel), [formatMessage]);

  const fieldType = useMemo<SettingsFieldType>(
    () => (activeTabId === ORGANIZATION_TAB_ID ? 'org_name' : 'legal_entity_name'),
    [activeTabId],
  );

  const handleOpenEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleCloseEdit = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleSaveName = useCallback(() => {
    // TODO(IDFE): Update the name via the account-management update.
    // For now this only closes the modal; the displayed value is not yet updated.
    setIsEditing(false);
  }, []);

  return (
    <VStack className="workspaceContentFadeIn" gap={3} width="100%" data-testid="settings-page">
      <Text as="h1" font="title1">
        {formatMessage(messages.title)}
      </Text>
      <EntityTabs
        legalEntities={legalEntities}
        activeTabId={activeTabId}
        onChange={setActiveTabId}
      />
      <SettingsRow
        icon="star"
        title={nameLabel}
        description={name}
        onClick={handleOpenEdit}
        testID="settings-display-name-row"
      />
      <EditFieldModal
        visible={isEditing}
        title={nameLabel}
        fieldType={fieldType}
        initialValue={name}
        onSave={handleSaveName}
        onClose={handleCloseEdit}
      />
    </VStack>
  );
}
